import { DataSource, useDataSources } from '@/composables/useDataSources'
import { DashboardComponentType, Measurement } from '@/features/dashboard'
import { computed } from 'vue'
import { ResourceType, useMeasurements } from './useMeasurements'
import { storeToRefs } from 'pinia'

export function useComponentData<T>(
  featureName: string,
  componentType: DashboardComponentType,
  resourceType: ResourceType
) {
  const { measurementSources } = storeToRefs(useMeasurements())
  const { getDataSources } = useDataSources()

  const measurements = computed<Measurement[]>((): Measurement[] =>
    measurementSources.value
      .filter((g) => g.type === componentType && g.componentName === featureName)
      .flat()
      .map((ms) => ms.measurement)
  )

  const dataSourceIds = computed(() =>
    measurements.value.map(
      (measurement) =>
        `measurements.${featureName}.${measurement.name}.${
          measurement.field.name
        }.${componentType.toLowerCase()}.${resourceType}`
    )
  )

  const dataSources = getDataSources<T>(dataSourceIds)

  const mergedDataSources = computed<DataSource<T>[]>(() => {
    return dataSources?.value
      ? Object.values(dataSources.value)
          .filter((dataSource): dataSource is DataSource<T> => dataSource !== undefined)
          .map((dataSource) => dataSource!)
      : []
  })

  const data = computed<T[]>(() => {
    const d = mergedDataSources.value.filter((ds) => ds.data !== undefined).map((ds) => ds.data!)
    if (d.length > 0 && d[0] instanceof Array) {
      return (d as T[][]).flat() as T[]
    }
    return d as T[]
  })

  const isLoading = computed(() => {
    return mergedDataSources.value.every((dataSource) => dataSource.isLoading)
  })

  return {
    data,
    isLoading,
    measurements
  }
}
